import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PointsNumberService } from '@rd/core/services/points-number/points-number.service';
import { isNotNull } from '@rd/utils/isNotNull';
import { NUMBER_ONLY_REGEX } from '@rd/utils/regex.utils';
import { debounceTime, filter, fromEvent, iif, map } from 'rxjs';

@UntilDestroy()
@Directive({
  standalone: true,
  selector: 'input[counterNumberFormatter]',
})
export class CounterNumberFormatterDirective implements OnInit, OnChanges {
  private pointsNumberService = inject(PointsNumberService);
  private inputElement = inject(ElementRef<HTMLInputElement>);

  @Input() min: number;

  @Input() max: number;

  @Input() step: number;

  @Input() shouldFormatValue: boolean;

  @Input() formatOnBlur: boolean;

  @Input() debounceTime = 500;

  @Input() currentValue: number;

  @Output() valueChange = new EventEmitter<number>();

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    const isMetaKey = event.metaKey || event.ctrlKey;

    if (
      isMetaKey ||
      event.key === 'Backspace' ||
      event.key === 'Delete' ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight' ||
      event.key === 'Tab'
    ) {
      return;
    }

    if (!NUMBER_ONLY_REGEX.test(event.key)) {
      event.preventDefault();
    }
  }

  ngOnChanges({ currentValue }: SimpleChanges): void {
    if (!isNaN(currentValue?.currentValue)) {
      const formattedValue = this.formatValue(
        (currentValue.currentValue ?? 0)?.toString(),
      );

      this.inputElement.nativeElement.value = this.formatNumber(formattedValue);
    }
  }

  ngOnInit(): void {
    if (this.shouldFormatValue) {
      this.formatNumberWhenUserIsTyping();
    }

    iif(
      () => this.formatOnBlur,
      fromEvent<FocusEvent>(this.inputElement.nativeElement, 'blur'),
      fromEvent<InputEvent>(this.inputElement.nativeElement, 'input').pipe(
        debounceTime(this.debounceTime),
      ),
    )
      .pipe(
        map((event) => (event.target as HTMLInputElement).value),
        untilDestroyed(this),
      )
      .subscribe((value) => {
        const formattedValue = this.formatValue(value);

        this.inputElement.nativeElement.value =
          this.formatNumber(formattedValue);

        this.valueChange.emit(formattedValue);
      });
  }

  private formatNumberWhenUserIsTyping() {
    fromEvent<InputEvent>(this.inputElement.nativeElement, 'input')
      .pipe(
        map((event) => (event.target as HTMLInputElement).value),
        map((value) => (value !== '' ? this.parseValue(value) : null)),
        filter((parsedValue) => {
          return !!parsedValue && !isNaN(parsedValue);
        }),
        filter(isNotNull),
        untilDestroyed(this),
      )
      .subscribe((value) => {
        this.inputElement.nativeElement.value = this.formatNumber(value);
      });
  }

  private parseValue(value: string) {
    return Number(value.replace(/\,/g, ''));
  }

  private isLowerThanMinimumValue(value: number): boolean {
    return value < this.min;
  }

  private isExceedingMaximumValue(value: number): boolean {
    return value > this.max;
  }

  private roundDown(value: number): number {
    const remainder = value % this.step;
    return value - remainder;
  }

  private formatNumber(value: number) {
    return this.shouldFormatValue && !isNaN(value)
      ? this.pointsNumberService.roundNumber(value)
      : value.toString();
  }

  formatValue(value: string): number {
    const parsedValue = this.parseValue(value);

    const formattedValue = this.formatOutboundValue(
      isNaN(parsedValue) ? this.currentValue : parsedValue,
    );

    return formattedValue;
  }

  private formatOutboundValue(currentValue: number): number {
    const roundedValue = this.roundDown(currentValue);

    if (this.isLowerThanMinimumValue(roundedValue)) {
      return this.min;
    }

    if (this.isExceedingMaximumValue(roundedValue)) {
      return this.max;
    }

    return roundedValue;
  }
}
